/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import DotGrid from "@/components/customCopmonent/DotGrid";
import { PageHeaderHero, PAGE_HERO_CLASS } from "@/components/customCopmonent/Text/PageHeaderHero";
import { SectionWrapper } from "../Clientele.page-primitives";
import { RevealOnScroll } from "@/components/SpecialProjectsPage.primitives";

import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom"; // âœ… NEW
import { useTranslation } from "react-i18next";
import ProductCard from "@/components/customCopmonent/ProductCard";
import Seo from "@/components/Seo";
import AcheShowcase, {
	type ShowcaseHighlight,
	type ShowcaseSpec,
} from "@/components/customCopmonent/AcheShowcase";
import {
	ShieldCheck,
	Flame,
	Activity,
	TrendingUp,
	Thermometer,
	Hammer,
	Wind,
} from "lucide-react";

// -----------------------------
// âœ… NEW: Industry Config
// -----------------------------
const INDUSTRIES = [
	"Power Generation",
	"Oil & Gas",
	"Marine & Offshore",
	"Water Treatment",
	"Plastic Injection Molding & Machine Tools",
	"HVAC Systems",
	"Chemical Processing",
	"Data Center",
	"Steel & Metallurgy",
	"Renewable Energy",
	"Process Industries (Pulp, Paper & Sugar)",
];

// slug â†’ label mapping
const INDUSTRY_SLUG_MAP: Record<string, string> = {
	"power-generation": "Power Generation",
	"oil-gas": "Oil & Gas",
	"marine-offshore": "Marine & Offshore",
	"water-treatment": "Water Treatment",
	"plastic-injection": "Plastic Injection Molding & Machine Tools",
	hvac: "HVAC Systems",
	"chemical-processing": "Chemical Processing",
	"data-center": "Data Center",
	"steel-metallurgy": "Steel & Metallurgy",
	"renewable-energy": "Renewable Energy",
	"process-industries": "Process Industries (Pulp, Paper & Sugar)",
};

// reverse mapping
const getSlugFromIndustry = (industry: string) => {
	return Object.entries(INDUSTRY_SLUG_MAP).find(
		([, value]) => value === industry,
	)?.[0];
};

// -----------------------------
// Showcase content for Recuperators
// -----------------------------
const RECUP_IMAGES: string[] = ["/images/finalProducts/Recuperator.png"];

// Icon order matches the highlight/spec keys resolved via i18n inside the component.
const RECUP_HIGHLIGHT_ICONS = [ShieldCheck, Flame, Activity];
const RECUP_SPEC_ICONS = [TrendingUp, Thermometer, Hammer, Wind];

const Recuperators = () => {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// -----------------------------
	// âœ… NEW: State
	// -----------------------------
	const [activeIndustry, setActiveIndustry] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	// -----------------------------
	// âœ… NEW: Debounce Logic
	// -----------------------------
	useEffect(() => {
		const t = setTimeout(() => {
			setDebouncedSearch(search.toLowerCase());
		}, 300);
		return () => clearTimeout(t);
	}, [search]);

	// -----------------------------
	// âœ… NEW: Read URL param on load
	// -----------------------------
	useEffect(() => {
		const industrySlug = searchParams.get("industry");
		if (industrySlug && INDUSTRY_SLUG_MAP[industrySlug]) {
			setActiveIndustry(INDUSTRY_SLUG_MAP[industrySlug]);
		}
	}, []);

	// -----------------------------
	// Page Data (UNCHANGED)
	// -----------------------------
	const pageData = {
		title: t("productPages.recuperators.hero.title"),
		bgimage: "/images/header/RECUPERATORS.JPG",

		description: t("productPages.recuperators.hero.tagline"),

		meta: t("productPages.recuperators.hero.meta", {
			returnObjects: true,
		}) as string[],

		projects: [
			{
				id: "20",
				title: "Steel Sector, Waste Heat Recovery",
				application: "Waste Heat Recovery Low Pressure Compact Design",
				capacity: "",
				image: "/images/products/Recuperato-removebg-preview.png",
				details: {
					Industry: "Steel Sector",
					"System Type": "Waste Heat Recovery",
					"Design Type": "Low Pressure Compact Design",
					Customization: "Customized Design",
					Performance: "Excellent Performance & High Durability",
					Protection: "Corrosion Protected",
				},
				aplication: [
					"Renewable Energy",
					"Industrial Heating",
					"Energy Efficiency",
				],
			},
		],
	};

	// -----------------------------
	// âœ… NEW: Filter + Search + Sort
	// -----------------------------
	const filteredProjects = useMemo(() => {
		let data = [...pageData.projects];

		// SEARCH
		if (debouncedSearch) {
			data = data.filter((item) => {
				const text = (
					item.title +
					" " +
					(item.application || "")
				).toLowerCase();

				const industries = (item.aplication || []).join(" ").toLowerCase();

				return (
					text.includes(debouncedSearch) || industries.includes(debouncedSearch)
				);
			});
		}

		// FILTER
		if (activeIndustry) {
			data = data.filter((item) =>
				(item.aplication || []).includes(activeIndustry),
			);
		}

		// SORT
		if (activeIndustry) {
			data.sort((a, b) => {
				const aMatch = (a.aplication || []).includes(activeIndustry) ? 1 : 0;
				const bMatch = (b.aplication || []).includes(activeIndustry) ? 1 : 0;
				return bMatch - aMatch;
			});
		}

		return data;
	}, [debouncedSearch, activeIndustry]);

	// -----------------------------
	// âœ… NEW: Handle Filter Click
	// -----------------------------
	const handleFilterClick = (industry: string) => {
		const isSame = activeIndustry === industry;

		if (isSame) {
			setActiveIndustry(null);
			setSearchParams({});
		} else {
			setActiveIndustry(industry);
			const slug = getSlugFromIndustry(industry);
			setSearchParams({ industry: slug || "" });
		}
	};

	// Localized showcase content (icons stay static; copy resolves via i18n).
	const highlightKeys = [
		"designStandards",
		"highTempMetallurgy",
		"thermalRelief",
	] as const;
	const specKeys = [
		"efficiencyGain",
		"gasTemperatures",
		"construction",
		"pressureDrop",
	] as const;

	const RECUP_HIGHLIGHTS: ShowcaseHighlight[] = highlightKeys.map((k, i) => ({
		icon: RECUP_HIGHLIGHT_ICONS[i],
		title: t(`productPages.recuperators.showcase.highlights.${k}.title`),
		body: t(`productPages.recuperators.showcase.highlights.${k}.body`),
	}));

	const RECUP_SPECS: ShowcaseSpec[] = specKeys.map((k, i) => ({
		icon: RECUP_SPEC_ICONS[i],
		feature: t(`productPages.recuperators.showcase.specs.${k}.feature`),
		capability: t(`productPages.recuperators.showcase.specs.${k}.capability`),
	}));

	return (
		<>
			<Seo
				titleKey="productPages.recuperators.seo.title"
				descriptionKey="productPages.recuperators.seo.description"
				path="/products/recuperators"
			/>
			<Navigation />

			{/* HERO */}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${pageData.bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
					/>
				</div>

				<PageHeaderHero
					title={pageData.title}
					description={pageData.description}
					meta={pageData.meta}
				/>
			</SectionWrapper>

			{/* -----------------------------
			    âœ… NEW: FILTER + SEARCH UI
			------------------------------ */}
			{/* <div className="max-w-7xl mt-10 mx-auto px-4 sm:px-6 lg:px-12 flex  items-center justify-center">
				
				<input
					type="text"
					placeholder="Search products..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="w-full mb-6 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3b0092]"
				/>

				<div className="flex 	flex-wrap items-center justify-center gap-3 pb-3">
						<button
							onClick={() => {
								setActiveIndustry(null);
								setSearchParams({});
							}}
							className={`px-4 py-2 rounded-full border text-sm transition-all ${
								!activeIndustry ?
									"bg-[#3b0092] text-white border-[#3b0092]"
								:	"bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
							}`}>
							All
						</button>

						{INDUSTRIES.map((industry) => {
							const isActive = activeIndustry === industry;

							return (
								<button
									key={industry}
									onClick={() => handleFilterClick(industry)}
									className={`whitespace-nowrap px-4 py-2 rounded-full border transition-all text-sm ${
										isActive ?
											"bg-[#3b0092] text-white border-[#3b0092]"
										:	"bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
									}`}>
									{industry}
								</button>
							);
						})}
					</div>
			</div> */}

			{/* PRODUCTS */}
			{/* <SectionWrapper className="pt-0">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
						{filteredProjects.map((project, index) => (
							<RevealOnScroll
								key={project.id}
								delay={index * 0.1}>
								<ProductCard
									product={project}
									index={index}
								/>
							</RevealOnScroll>
						))}
					</div>

					{filteredProjects.length === 0 && (
						<p className="text-center text-gray-500 mt-10">
							No matching projects found.
						</p>
					)}
				</div>
			</SectionWrapper> */}

			<AcheShowcase
				title={
					<>
						{t("productPages.recuperators.showcase.titleMain")}{" "}
						<span
							className="italic font-medium"
							style={{ color: "#3b0092" }}>
							{t("productPages.recuperators.showcase.titleAccent")}
						</span>
					</>
				}
				description={(() => {
					const company = t(
						"productPages.recuperators.showcase.descriptionLeadCompany",
					);
					const parts = t("productPages.recuperators.showcase.description", {
						company,
					}).split(company);
					return (
						<>
							{parts[0]}
							<span
								className="font-semibold"
								style={{ color: "#3b0092" }}>
								{company}
							</span>
							{parts.slice(1).join(company)}
						</>
					);
				})()}
				images={RECUP_IMAGES}
				imageAlt={t("productPages.recuperators.showcase.imageAlt")}
				wordmarkPrimary="RECUP"
				wordmarkSecondary="POSITRON"
				compliancePill="Gas-to-Gas Â· 1000Â°C+"
				captionEyebrow="Waste Heat Recovery"
				captionTitle="Recuperator"
				eyebrow={t("productPages.recuperators.showcase.eyebrow")}
				specsTitle={t("productPages.recuperators.showcase.specsTitle")}
				specsEyebrow={t("productPages.recuperators.showcase.specsEyebrow")}
				highlights={RECUP_HIGHLIGHTS}
				specs={RECUP_SPECS}
				specsFooterLabel="Positron Engineering Â· Recuperator Series"
			/>

			<FooterSection />
		</>
	);
};

export default Recuperators;
